/**
 * Language service bridge for the web editor.
 *
 * The intelligence lives in PalmSens.MethodScript.Core and is reached through the WebAssembly
 * runtime that `public/methodscript-formatter-wasm.js` installs on `window`. This adapts that
 * global to the shared package's contract.
 */

import type {
    MethodScriptCompletionItem,
    MethodScriptHoverDocumentation,
    MethodScriptLanguageService,
    MethodScriptSignatureHelp,
} from 'shared-monaco-methodscript';

type WasmLanguageServiceGlobal = {
    getCompletions?: (code: string, lineNumber: number, column: number) => Array<MethodScriptCompletionItem>;
    getHover?: (word: string, lineText: string, wordStart: number) => MethodScriptHoverDocumentation | null;
    getSignatureHelp?: (lineText: string, cursorColumn: number) => MethodScriptSignatureHelp | null;
};

function wasm(): WasmLanguageServiceGlobal | undefined {
    return (window as Window & { MethodScriptFormatterWASM?: WasmLanguageServiceGlobal }).MethodScriptFormatterWASM;
}

/**
 * The web editor's language service. Each call degrades to an empty result while the WebAssembly
 * runtime is still starting, which the editor treats as "nothing to suggest".
 */
export const webEditorLanguageService: MethodScriptLanguageService = {
    getCompletions: (code, lineNumber, column) => wasm()?.getCompletions?.(code, lineNumber, column) ?? [],
    getHover: (word, lineText, wordStart) => wasm()?.getHover?.(word, lineText, wordStart) ?? null,
    getSignatureHelp: (lineText, cursorColumn) => wasm()?.getSignatureHelp?.(lineText, cursorColumn) ?? null,
};
